import React, { useEffect, useMemo, useState } from "react";
import { listInternships, deleteInternship, withBase } from "./api";
import styles from "./List.module.css";

/* ---- tiny inline icons (no extra deps) ---- */
const CalendarIcon = () => (
  <svg className={styles.icon} width="14" height="14" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="M3 9H21" stroke="currentColor" strokeWidth="2" />
    <path d="M8 3V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M16 3V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const PinIcon = () => (
  <svg className={styles.icon} width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 22s7-7.58 7-12.5A7 7 0 0 0 5 9.5C5 14.42 12 22 12 22Z"
      stroke="currentColor"
      strokeWidth="2"
    />
    <circle cx="12" cy="9.5" r="2.3" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const EditIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 20h9"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
    />
    <path
      d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DeleteIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <path d="M3 6h18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    <path
      d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SortIcon = ({ active, dir }) => (
  <span className={styles.sortIcon}>
    <svg
      width="9"
      height="6"
      viewBox="0 0 10 6"
      className={active && dir === "asc" ? styles.sortIconActive : ""}
      style={{ marginBottom: 1 }}
    >
      <path d="M5 0 10 6 0 6Z" fill="currentColor" />
    </svg>
    <svg
      width="9"
      height="6"
      viewBox="0 0 10 6"
      className={active && dir === "desc" ? styles.sortIconActive : ""}
    >
      <path d="M5 6 0 0 10 0Z" fill="currentColor" />
    </svg>
  </span>
);

const COLUMNS = [
  { key: "idx", label: "#", sortable: false },
  { key: "title", label: "Title", sortable: true },
  { key: "description", label: "Description", sortable: false },
  { key: "year_of_passing", label: "Exp.", sortable: true },
  { key: "qualification", label: "Education", sortable: true },
  { key: "last_date", label: "Last Date", sortable: true },
  { key: "contact_email", label: "Email", sortable: false },
  { key: "office_location", label: "Location", sortable: true },
  { key: "logo_path", label: "Image", sortable: false },
  { key: "action", label: "Action", sortable: false },
];

export default function InternshipsList() {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    listInternships()
      .then((res) => setItems(res.items || []))
      .catch((e) => setError(e.message || "Failed to load"));
  }, []);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortedItems = useMemo(() => {
    if (!sortKey) return items;
    const copy = [...items];
    copy.sort((a, b) => {
      const av = (a[sortKey] ?? "").toString().toLowerCase();
      const bv = (b[sortKey] ?? "").toString().toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [items, sortKey, sortDir]);

  return (
    <div className={`${styles.card} ${styles.compactAdminTable}`}>
      <div className={styles.headerRow}>
        <div>
          <div className={styles.title}>Internships</div>
          <div className={styles.subtitle}>Manage active internship openings</div>
        </div>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead className={styles.thead}>
            <tr>
              {COLUMNS.map((col) => (
                <th key={col.key} className={styles.th}>
                  {col.sortable ? (
                    <span
                      className={styles.thSortable}
                      onClick={() => handleSort(col.key)}
                    >
                      {col.label}
                      <SortIcon active={sortKey === col.key} dir={sortDir} />
                    </span>
                  ) : (
                    col.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={styles.tbody}>
            {error ? (
              <tr>
                <td className={styles.empty} colSpan={COLUMNS.length}>
                  {error}
                </td>
              </tr>
            ) : sortedItems.length === 0 ? (
              <tr>
                <td className={styles.empty} colSpan={COLUMNS.length}>
                  No internships found.
                </td>
              </tr>
            ) : (
              sortedItems.map((it, i) => (
                <tr key={it.id}>
                  <td className={styles.td}>
                    <span className={styles.indexBadge}>
                      {i + 1}
                    </span>
                  </td>
                  <td className={`${styles.td} ${styles.jobTitle}`}>{it.title}</td>
                  <td className={`${styles.td} ${styles.description}`} title={it.description}>
                    {it.description}
                  </td>
                  <td className={styles.td}>
                    <span className={styles.expBadge}>{it.year_of_passing}</span>
                  </td>
                  <td className={styles.td}>{it.qualification}</td>
                  <td className={styles.td}>
                    <span className={styles.dateCell}>
                      <CalendarIcon />
                      {it.last_date}
                    </span>
                  </td>
                  <td className={`${styles.td} ${styles.emailCell}`}>{it.contact_email}</td>
                  <td className={styles.td}>
                    <span className={styles.locationCell}>
                      <PinIcon />
                      {it.office_location}
                    </span>
                  </td>
                  <td className={styles.td}>
                    <span className={styles.logoBox}>
                      {it.logo_path ? (
                        <img
                          className={styles.logoImg}
                          src={withBase(it.logo_path)}
                          alt="logo"
                        />
                      ) : (
                        <span className={styles.logoFallback}>No image</span>
                      )}
                    </span>
                  </td>
                  <td className={styles.td}>
                    <div className={styles.actions}>
                      <button
                        className={`${styles.btn} ${styles.btnEdit}`}
                        onClick={() => {
                          const params = new URLSearchParams({ id: String(it.id) });
                          window.location.href = `/admin/internships/add?${params.toString()}`;
                        }}
                      >
                        <EditIcon /> Edit
                      </button>
                      <button
                        className={`${styles.btn} ${styles.btnDelete}`}
                        disabled={busyId === it.id}
                        onClick={async () => {
                          setBusyId(it.id);
                          try {
                            await deleteInternship(it.id);
                            setItems(items.filter((x) => x.id !== it.id));
                          } catch (e) {
                            setError(e.message || "Delete failed");
                          } finally {
                            setBusyId(null);
                          }
                        }}
                      >
                        <DeleteIcon /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
}
